import type {
  CardHeaderProps,
  GridProps
} from '@chakra-ui/react'
import {
  Button,
  Flex,
  Grid,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react'
import {
  type InsuranceParamsDto,
  type InsuranceParamsPropertyDto,
  type InsuranceParamsTravelDto,
  type InsurerDiscount
} from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import {
  PropertyValidationRuleObject,
  TravelAddonValidationSchema,
  TravelValidationBaseObject,
  VehicleBaseParamsValidationSchema,
} from 'BrokerTsLib/src/validation'
import { forwardRef, useEffect, useState } from 'react'
import * as yup from 'yup'

import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { useGenerateMakeInsuranceOffers } from '@/hooks/useOffer'

import { CustomCollapse } from 'BrokerTsLib/src/components'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { VehicleExtraBaseParamsValidationSchema } from 'BrokerTsLib/src/validation/vehicle-extra'
import { FormBody } from '../../containers/forms/FormBody'
import { FormSection } from '../../containers/forms/FormSection'
import { ParametersForm as PropertyParametersForm } from '../../containers/steps/property/Step2/ParametersForm'
import { ParametersForm as TravelParametersForm } from '../../containers/steps/travel/Step2/forms/ParametersForm'
import { ParametersForm as ExtraParametersForm } from '../../containers/steps/vehicle/extra/Step2/ParametersForm'
import { ParametersForm as VehicleParametersForm } from '../../containers/steps/vehicle/Step2/forms/ParametersForm'
import type { ICalculationState } from '../../containers/steps/vehicle/Step2/Step2'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useSaveBaseParams } from '../../hooks/useWizard'
import ErrorSummary from '../ErrorSummary'

export interface ParametersProps extends GridProps {
  id: string
  calculationState: ICalculationState
  onCalculateOffers?: () => Promise<void>
  innerRef?: React.Ref<HTMLDivElement>
  isCollapsed: boolean
  cardHeaderProps?: CardHeaderProps
}

export const vehicleBaseSchema = yup.object().shape({
  baseParams: VehicleBaseParamsValidationSchema as any,
})

export const travelBaseSchema = yup.object().shape({
  travel: yup.object().shape(TravelValidationBaseObject),
  addons: TravelAddonValidationSchema,
})


export const propertyBaseSchema = yup.object().shape({
  property: yup.object().shape(PropertyValidationRuleObject),
})

export const liabilityBaseSchema = yup.object().shape({
  property: yup.object().shape({
    ...PropertyValidationRuleObject,
    data: yup.object().shape({}),
    type: yup.string().nullable().optional(),
    range: yup.string().nullable().optional(),
  }),
})

export const vehicleExtraBaseSchema = yup.object().shape({
  baseParams: VehicleExtraBaseParamsValidationSchema
})

const getSchema = (product: Product, app: Apps) => {
  switch (product) {
    case Product.TravelInsurance:
      return travelBaseSchema
    case Product.LiabilityInsurance:
      return liabilityBaseSchema
    case Product.PropertyInsurance:
      return propertyBaseSchema
    default:
      if (app == Apps.extra)
        return vehicleExtraBaseSchema
      else
        return vehicleBaseSchema
  }
}

export const Parameters = forwardRef(
  (
    {
      id,
      calculationState,
      onCalculateOffers,
      innerRef,
      isCollapsed,
      cardHeaderProps,
      ...rest
    }: ParametersProps,
    ref,
  ) => {
    const { translate } = useTranslationsContext()
    const { product, app } = useApp()
    const {
      insuranceParamsId,
      insuranceParams,
      refetchInsuranceParams,
      isDisabled,
      isSubaruInsurance,
      isMazdaInsurance,
      isKiaInsurance,
      isMgInsurance
    } = useInsuranceParams()
    
    const [discounts, setDiscounts] = useState<InsurerDiscount[] | null>(app === Apps.publicPoleas ? [] : null)
    const { mutateAsync: saveBaseParams, isPending } = useSaveBaseParams(insuranceParamsId!)

    const methods = useForm<InsuranceParamsDto | InsuranceParamsTravelDto | InsuranceParamsPropertyDto>({
      defaultValues: insuranceParams,
      resolver: yupResolver(getSchema(product!, app!) as any) as any,
    })

    const {
      handleSubmit,
      formState: { errors },
      setError,
      reset
    } = methods

    const handleCalculateClick = () => onCalculateOffers?.()

    const onSubmit = handleSubmit(
      (data: InsuranceParamsDto | InsuranceParamsTravelDto | InsuranceParamsPropertyDto) => {
        return saveBaseParams(data)
          .then(() => {
            reset(data)

            handleCalculateClick()?.then(() => refetchInsuranceParams())
          })
          .catch((err) => {
            if (
              !(
                err.response &&
                err.response.status === 400 &&
                err.response.data
              )
            )
              return

            const data = err.response.data

            // zobrazeni serverove validace
            Object.keys(data).forEach((x: any) => {
              setError(global.toCamelCase(x), { message: data[x].join(' ') })
            })
          })
      },
      (err) => {
        console.log('validation error', err)
      }
    )

    const handleEditParamsClick = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      if (cardHeaderProps?.onClick) {
        cardHeaderProps.onClick(event as any)
      }
    }

    const { mutateAsync: generateMakeInsuranceOffers, isPending: isLoadingMakeInsurance } = useGenerateMakeInsuranceOffers()
    const resolveMakeInsuranceOffers = async () => {
      const createdOfferIds = await generateMakeInsuranceOffers(insuranceParamsId!)

      if (createdOfferIds.data.length > 0) {
        const ipvData = await refetchInsuranceParams()
        if (ipvData.isSuccess)
          reset({ ...ipvData.data.data, baseParams: { ...ipvData.data.data.baseParams, insurerDiscounts: discounts! } })

        onSubmit()
      }
    }

    // generování značkových pojištění (Subaru, Mazda ,...)
    useEffect(() => {
      if ((isSubaruInsurance || isMazdaInsurance || isKiaInsurance || isMgInsurance) && discounts && !isLoadingMakeInsurance)
        resolveMakeInsuranceOffers()
    }, [isSubaruInsurance, isMazdaInsurance, isKiaInsurance, isMgInsurance, !discounts])

    return (
      <FormSection
        number={1}
        ref={innerRef}
        sectionTitle={
          <HStack w="100%" justifyContent="space-between">
            <Text>{translate('wizard.step2.parameters', 'Parametry pojištění')}</Text>

            {isCollapsed && cardHeaderProps?.onClick && !isDisabled && (
              <Button onClick={handleEditParamsClick}>
                {translate('wizard.step2.editParameters', 'Upravit parametry pojištění')}
              </Button>
            )}
          </HStack>
        }
        id={id}
        cardHeaderProps={cardHeaderProps}
        bodyProps={{
          sx: {
            pb: {
              base: 8,
              md: isCollapsed ? 0 : 12,
            },
          },
        }}
        footer={
          !isCollapsed && !isDisabled && (
            <Stack alignItems="center" w="100%" gap={8}>
              <Flex sx={{ w: '100%', justifyContent: 'center' }}>
                <Button
                  colorScheme="primary"
                  isLoading={calculationState.isCalculating || isPending || !discounts}
                  onClick={onSubmit}
                >
                  {translate('wizard.step2.requestQuotes', 'Poptat pojištění')}
                </Button>
              </Flex>
              <ErrorSummary errors={errors} />
            </Stack>
          )
        }
        {...rest}
      >
        <CustomCollapse in={!isCollapsed}>
          <Grid
            sx={{
              gap: 12,
            }}
          >
            <FormProvider {...methods}>
              <form onSubmit={onSubmit}>
                <FormBody>
                  {app == Apps.travel && <TravelParametersForm onDiscountsLoaded={(discounts) => setDiscounts(discounts)} />}

                  {app == Apps.property && <PropertyParametersForm onDiscountsLoaded={(discounts) => setDiscounts(discounts)} />}

                  {isIn(app!, Apps.vehicle, Apps.leasing, Apps.publicPoleas) && <VehicleParametersForm onDiscountsLoaded={(discounts) => setDiscounts(discounts)} />}

                  {app == Apps.extra && <ExtraParametersForm/>}
                </FormBody>
              </form>
            </FormProvider>
          </Grid>
        </CustomCollapse>
      </FormSection>
    )
  },
)
